import React, { useState, useRef, useRef as useReactRef } from "react";
import TransactionStatement from "./TransactionStatement";
import { scrollTriggerIntoView } from '../../util/modalUtils';
import LinkSuccessModal from "./LinkSuccessModal";
import html2pdf from "html2pdf.js";
import { Modal, Table, Dropdown, Menu, Select, DatePicker, Spin } from "antd";
import { useLocation } from "react-router-dom";
import { api } from '../../lib/api-client';
import PageHeader from "../../layouts/PageHeader";
import { useModalWidth } from '../../hooks/useModalWidth';

// Support both prop and navigation state
export default function TransactionsPage(props) {
      const modalWidth = useModalWidth();
      // Pagination state for transactions table
      const [currentPage, setCurrentPage] = useState(1);
      const pageSize = 10;
    const [showDownloadSuccessModal, setShowDownloadSuccessModal] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [isStatementGenerating, setIsStatementGenerating] = useState(false);
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);
  const location = useLocation();
  // Try prop, then navigation state, then fallback
  const productDetails = props.productDetails || (location.state && location.state.productDetails) || {};
  const [showSearch, setShowSearch] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const statementPdfRef = useReactRef();
  const searchInputRef = useRef(null);
  const [dateFilter, setDateFilter] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [statementYear, setStatementYear] = useState([]);
  const [statementError, setStatementError] = useState('');
  const [customDateRange, setCustomDateRange] = useState([null, null]);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);



  // Transactions: from navigation state or prop
  // Normalize different API shapes to the table row shape: { date, transaction, reference, amount }
  const transactions = React.useMemo(() => {
  const raw = (location.state && location.state.transactions) || props.transactions || window.__fetchedTransactions || [];

    // If raw is an object with { success, data: [...] }
    const input = raw && raw.data && Array.isArray(raw.data)
      ? raw.data
      : Array.isArray(raw) ? raw : [];

    return input.map(item => {
      // API fields: payment_date, created_on, trans_type, receipt_number, received, id, comment
      const date = item.payment_date || item.created_on || item.date || '';
      const transaction = item.trans_type || item.transaction || item.comment || '';
      const reference = item.receipt_number || item.reference || item.id || '';
      // amount: prefer received, then net_premium, money_allocated, amount
      const amount = (
        typeof item.received !== 'undefined' ? item.received :
        typeof item.net_premium !== 'undefined' ? item.net_premium :
        typeof item.money_allocated !== 'undefined' ? item.money_allocated :
        typeof item.amount !== 'undefined' ? item.amount : 0
      );

      return {
        date,
        transaction,
        reference: String(reference),
        amount: Number(amount)
      };
    });
  }, [location.state, props.transactions]);

  // If we were navigated with a transactionsEndpoint but no transactions payload, fetch them
  React.useEffect(() => {
    const endpoint = location.state && location.state.transactionsEndpoint;
    const provided = location.state && location.state.transactions;
    if (endpoint && !provided) {
      let cancelled = false;
      (async () => {
        try {
          setTableLoading(true);
          const res = await api.get(endpoint);
          if (cancelled) return;
          // If API returns { success, data: [...] } or an array, set into location state fallback via history.replace is not ideal here.
          // Instead, mutate a localRef via state is tricky; we'll store fetched transactions in props.transactions if caller passed a setter.
          // Simpler: replace location.state.transactions by using history.replace so memo picks it up — but router history API can vary.
          // To keep this local and safe, we'll set a local ref via a small state to re-render.
          // We'll use a global window.__fetchedTransactions as a temporary fallback for the memo (not ideal but low-risk local change).
          if (res && res.data) {
            window.__fetchedTransactions = Array.isArray(res.data) ? res.data : (res.data.data || []);
          } else if (Array.isArray(res)) {
            window.__fetchedTransactions = res;
          } else {
            window.__fetchedTransactions = [];
          }
          // force re-render
          setSearchValue(s => s + '');
        } catch (err) {
          console.error('Failed to fetch transactions from endpoint', endpoint, err);
          window.__fetchedTransactions = [];
          setSearchValue(s => s + '');
        } finally {
          setTableLoading(false);
        }
      })();
      return () => { cancelled = true; };
    }
  }, [location.state]);

  // Sort transactions by date descending
  const sortedTransactions = [...transactions].sort((a, b) => new Date(b.date) - new Date(a.date));

  // Transactions to include in the statement PDF based on selected statementYear(s)
  const statementFilteredTransactions = React.useMemo(() => {
    if (!statementYear || statementYear.length === 0) return [];
    // If user selected 'all', include everything
    if (statementYear.includes('all')) return sortedTransactions;
    const yearsSet = new Set(statementYear.map(y => Number(y)));
    return sortedTransactions.filter(t => {
      if (!t || !t.date) return false;
      const d = new Date(t.date);
      if (Number.isNaN(d.getTime())) return false;
      return yearsSet.has(d.getFullYear());
    });
  }, [sortedTransactions, statementYear]);

  // Strict columns: Date, Amount, Reference
  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      align: 'center', // header centered
      render: (date) => (
        <div style={{ textAlign: 'left', paddingLeft: 8 }}>{formatDate(date)}</div>
      ),
    },
    {
      title: 'Transaction',
      dataIndex: 'transaction',
      key: 'transaction',
      align: 'left',
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      align: 'left',
    },
   
     {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
        align: 'center',
       render: (amount) => (
         <span style={{ color: '#111', fontWeight: 600 }}>
           GHS {Math.abs(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
         </span>
       ),
    },
  ];

  const isDateFilterActive = !!dateFilter;
  const isSearchActive = !!searchValue;

  const handleSearchClick = () => {
    setShowSearch((prev) => !prev);
    setTimeout(() => {
      if (!showSearch && searchInputRef.current) searchInputRef.current.focus();
    }, 200);
  };

  // ...existing code...

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleDateMenuClick = ({ key }) => {
    if (isSearchActive) return;
    setDateFilter(key);
    if (key === 'custom') {
      setShowCustomDatePicker(true);
    } else {
      setShowCustomDatePicker(false);
      setCustomDateRange([null, null]);
    }
  };

  // ...existing code...

  const dateMenu = (
    <Menu onClick={handleDateMenuClick} selectedKeys={[dateFilter]} style={{ minWidth: 160 }}>
      <Menu.Item key="week" style={{ fontWeight: 500 }} disabled={isSearchActive}>This Week</Menu.Item>
      <Menu.Item key="month" style={{ fontWeight: 500 }} disabled={isSearchActive}>This Month</Menu.Item>
      <Menu.Item key="year" style={{ fontWeight: 500 }} disabled={isSearchActive}>This Year</Menu.Item>
      <Menu.Item key="custom" style={{ fontWeight: 500 }} disabled={isSearchActive}>Custom</Menu.Item>
    </Menu>
  );

  let filteredTransactions = sortedTransactions;
    // Paginate filteredTransactions for table display
    const paginatedTransactions = React.useMemo(() => {
      const startIdx = (currentPage - 1) * pageSize;
      return filteredTransactions.slice(startIdx, startIdx + pageSize);
    }, [filteredTransactions, currentPage]);
  if (isDateFilterActive && !isSearchActive) {
    let start, end;
    const today = new Date();
    if (dateFilter === 'week') {
      end = today;
      start = new Date();
      start.setDate(today.getDate() - 6);
    } else if (dateFilter === 'month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = today;
    } else if (dateFilter === 'year') {
      start = new Date(today.getFullYear(), 0, 1);
      end = today;
    } else if (
      dateFilter === 'custom' &&
      Array.isArray(customDateRange) &&
      customDateRange[0] &&
      customDateRange[1]
    ) {
      start = customDateRange[0].toDate ? customDateRange[0].toDate() : customDateRange[0];
      end = customDateRange[1].toDate ? customDateRange[1].toDate() : customDateRange[1];
    }
    if (start && end) {
      filteredTransactions = filteredTransactions.filter(t => {
        const d = new Date(t.date);
        return d >= start && d <= end;
      });
    }
  } else if (isSearchActive) {
    const val = searchValue.trim().toLowerCase();
    filteredTransactions = filteredTransactions.filter(t =>
      t.reference.toLowerCase().includes(val) ||
      t.amount.toString().includes(val)
    );
  }

  const getLast5Years = () => {
    const years = [];
    const currentYear = new Date().getFullYear();
    for (let i = 0; i < 5; i++) years.push(currentYear - i);
    return years;
  };

  const handleStatementDownload = () => {
    if (!statementYear || statementYear.length === 0) {
      setStatementError('Please select at least one year or All Years');
      return;
    }
    setStatementError('');
    setModalOpen(false);
    setIsStatementGenerating(true);
    setTimeout(() => {
      setShowStatementModal(true);
      setIsStatementGenerating(false);
    }, 300);
  };

  const waitForImages = (container) => {
    const imgs = container.querySelectorAll('img');
    return Promise.all(Array.from(imgs).map(img => (img.complete ? Promise.resolve() : new Promise(resolve => { img.onload = img.onerror = resolve; }))));
  };

  const handlePdfDownload = async () => {
    if (statementPdfRef.current) {
      const node = statementPdfRef.current.querySelector('.pdf-page') || statementPdfRef.current;
      const prevWidth = node.style.width;
      try {
        setIsPdfDownloading(true);
        node.style.width = '210mm';
        await new Promise(r => setTimeout(r, 60));
        await waitForImages(node);

        await html2pdf()
          .set({
            margin: 10,
            filename: `Transaction_Statement.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true, logging: false },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
            pagebreak: { mode: ['css', 'legacy'] }
          })
          .from(node)
          .save();

        setShowDownloadSuccessModal(true);
      } catch (err) {
        console.error('Failed to generate PDF', err);
      } finally {
        node.style.width = prevWidth;
        setIsPdfDownloading(false);
      }
    }
  };

  // Ensure statement-related modals are scrolled into view when opened
  React.useEffect(() => {
    if (showStatementModal || showDownloadSuccessModal) scrollTriggerIntoView();
  }, [showStatementModal, showDownloadSuccessModal]);

  return (
    <>
      <LinkSuccessModal
        visible={showDownloadSuccessModal}
        onClose={() => setShowDownloadSuccessModal(false)}
        title="Download Successful"
        subTitle="Your transaction statement PDF has been downloaded."
        closeText="OK"
      />
      <div className="container p-0">
        <PageHeader />
        <div className="py-4 d-flex flex-column align-items-center">
          <div className="transactions-header-container mb-3 w-100 d-flex align-items-center justify-content-between" style={{ paddingLeft: 24, paddingRight: 24 }}>
            <div className="d-flex align-items-center gap-2">
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search by amount or reference..."
                  className="form-control border-0"
                  style={{
                    background: 'transparent',
                    borderRadius: 24,
                    paddingLeft: 16,
                    paddingRight: 36,
                    height: 36,
                    width: showSearch ? 300 : 0,
                    minWidth: showSearch ? 120 : 0,
                    opacity: showSearch ? 1 : 0,
                    transition: 'width 0.3s cubic-bezier(.4,2,.6,1), opacity 0.2s',
                    boxShadow: 'none',
                    border: 'none',
                    pointerEvents: showSearch ? 'auto' : 'none',
                    textAlign: 'left',
                  }}
                  value={searchValue}
                  onChange={e => setSearchValue(e.target.value)}
                  onBlur={() => setShowSearch(false)}
                  disabled={isDateFilterActive}
                />
                <i
                  className="bi bi-search"
                  style={{ fontSize: 20, cursor: 'pointer', zIndex: 2, padding: 8, borderRadius: '50%', transition: 'background 0.2s', position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: showSearch ? '#f7f7fa' : 'transparent' }}
                  aria-label="Search"
                  onClick={handleSearchClick}
                  tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleSearchClick(); }}
                  role="button"
                ></i>
              </div>
              <button className="btn btn-primary ms-2" style={{ borderRadius: 24, height: 36, fontWeight: 600, minWidth: 120 }} onClick={() => setModalOpen(true)}>Generate Statement</button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', background: '#fff', borderRadius: 32, boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)', padding: '4px 8px', minHeight: 44 }}>
                  <Dropdown overlay={dateMenu} trigger={["click"]} placement="bottomRight">
                    <button className="btn btn-light border-0 d-flex align-items-center justify-content-center ms-1" style={{ borderRadius: 24, height: 36, background: 'transparent', fontWeight: 500, display: 'flex', alignItems: 'center', minWidth: 90, padding: '0 12px' }} title="Filter by date" disabled={isSearchActive}>
                      <span style={{ whiteSpace: 'nowrap', marginRight: 8 }}>Date</span>
                      <i className="bi bi-calendar" style={{ fontSize: 20 }}></i>
                    </button>
                  </Dropdown>
                </div>
              </div>
              {/* Custom Date Range Picker */}
              {showCustomDatePicker && (
                <div style={{ marginRight: 12 }}>
                  <DatePicker.RangePicker
                    value={Array.isArray(customDateRange) ? customDateRange : [null, null]}
                    onChange={vals => {
                      if (!vals) {
                        setCustomDateRange([null, null]);
                        setDateFilter("");
                        setShowCustomDatePicker(false);
                      } else {
                        setCustomDateRange(vals);
                        if (vals[0] && vals[1]) setDateFilter('custom');
                      }
                    }}
                    allowClear
                    style={{ minWidth: 220, marginBottom: 0 }}
                    disabled={isSearchActive}
                    format="YYYY-MM-DD"
                  />
                </div>
              )}
              <Modal
                open={modalOpen}
                onCancel={() => { setModalOpen(false); setStatementYear(''); setStatementError(''); }}
                footer={null}
                centered
                width={modalWidth}
                closable={false}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <span className="fw-bold">Generate Statement</span>
                    <button
                      onClick={() => { setModalOpen(false); setStatementYear(''); setStatementError(''); }}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: 26,
                        color: '#888',
                        cursor: 'pointer',
                        position: 'absolute',
                        top: 16,
                        right: 24,
                        zIndex: 10
                      }}
                      aria-label="Close"
                    >×</button>
                  </div>
                }
                bodyStyle={{ padding: '18px 24px 18px 24px', minHeight: 180, display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative' }}
              >
                <div style={{ marginBottom: 16 }}>
                  <p style={{ marginBottom: 16, fontSize: 13 }}>Select the consecutive years you want to generate a statement for</p>
                  <div className="mb-3">
                    <div className="mt-3">
                      <label className="form-label fw-bold" style={{ display: 'block', marginBottom: 10, fontSize: 15 }}>Select Year(s)</label>
                      <Select
                        mode="multiple"
                        allowClear
                        placeholder="Select Year(s)"
                        value={statementYear}
                        onChange={setStatementYear}
                        style={{ width: '100%', minWidth: 220, marginBottom: 12 }}
                        optionLabelProp="label"
                      >
                        <Select.Option value="all" label="All Years">All Years</Select.Option>
                        {getLast5Years().map(year => (
                          <Select.Option key={year} value={year} label={year}>{year}</Select.Option>
                        ))}
                      </Select>
                    </div>
                    {statementError && <div className="text-danger mt-2">{statementError}</div>}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <button className="btn btn-primary mb-4 w-80 mobile-full-width" disabled={isStatementGenerating} onClick={handleStatementDownload}>
                    {isStatementGenerating ? <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}><Spin size="small" /> Generating...</span> : 'Preview & Download PDF'}
                  </button>
                </div>
              </Modal>

              {/* Statement PDF Preview Modal */}
              <Modal
                open={showStatementModal}
                onCancel={() => setShowStatementModal(false)}
                footer={null}
                centered
                width={Math.max(modalWidth, 900)}
                title={<div className="fw-bold">Transaction Statement Preview</div>}
                bodyStyle={{ padding: 0, background: '#ffffffff' }}
              >
                <div style={{ padding: 24, background: '#ffffffff' }}>
                  <div style={{ position: 'relative' }}>
                    <div ref={statementPdfRef}>
                      <TransactionStatement
                        companyName={productDetails.owner || productDetails.policyName || ''}
                        data={statementFilteredTransactions}
                        companyId={productDetails.companyId || ''}
                      />
                    </div>
                    {isPdfDownloading && (
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)', zIndex: 40 }}>
                        <div style={{ textAlign: 'center' }}><Spin size="large" /><div style={{ marginTop: 8 }}>Preparing PDF...</div></div>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
                    <button className="btn btn-primary mb-4 w-80 mobile-full-width" disabled={isPdfDownloading} onClick={handlePdfDownload}>
                      {isPdfDownloading ? <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}><Spin size="small" /> Downloading...</span> : 'Download As PDF'}
                    </button>
                  </div>
                </div>
              </Modal>
            </div>
          </div>
          <div className="w-100 d-flex justify-content-center" style={{ minHeight: 120, background: 'transparent' }}>
              <div className="card p-0 shadow-sm" style={{ width: '97%', maxWidth: 1330, minWidth: 320, color: '#111', overflowX: 'auto', borderRadius: 10, position: 'relative' }}>
                {tableLoading && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)', zIndex: 20 }}>
                    <Spin size="large" />
                  </div>
                )}
                <Table
                  columns={columns}
                  dataSource={paginatedTransactions}
                  rowKey={(record) => record.reference}
                  pagination={false}
                  locale={{ emptyText: <span className="text-muted" style={{ fontSize: 18 }}>No transactions found.</span> }}
                  style={{ overflow: 'hidden', borderRadius: 24 }}
                />
                <style>{`
                  @media (max-width: 600px) {
                    .ant-table-tbody > tr > td:first-child div {
                      padding-left: 16px !important;
                    }
                  }
                `}</style>
              </div>
          </div>
          <div className="w-100 d-flex justify-content-center mt-2">
            <div className="d-flex justify-content-between align-items-center px-4 py-3" style={{ maxWidth: 1330, minWidth: 320, width: '100%', borderRadius: 10, border: 'none', background: 'transparent' }}>
              <div className="text-muted" style={{ fontSize: 15, fontWeight: 500 }}>
                Showing 1-{filteredTransactions.length} of {filteredTransactions.length} results
              </div>
              <CustomPagination
                current={currentPage}
                pageSize={pageSize}
                total={filteredTransactions.length}
                onChange={setCurrentPage}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function CustomPagination({ current, pageSize, total, onChange }) {
  const pageCount = Math.ceil(total / pageSize);
  const pages = Array.from({ length: pageCount }, (_, i) => i + 1);
  const navBtnStyle = {
    background: 'transparent',
    color: '#184b18',
    border: 'none',
    borderRadius: 0,
    padding: '0 10px',
    minWidth: 0,
    height: 26,
    fontWeight: 700,
    fontSize: 14,
    outline: 'none',
    cursor: 'pointer',
    transition: 'color 0.2s',
    boxShadow: 'none',
    margin: '0 2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 1,
  };
  const navBtnDisabledStyle = {
    ...navBtnStyle,
    color: '#bdbdbd',
    cursor: 'not-allowed',
    opacity: 0.6,
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <button
        onClick={() => current > 1 && onChange(current - 1)}
        disabled={current === 1}
        style={current === 1 ? navBtnDisabledStyle : navBtnStyle}
        aria-label="Previous page"
      >
        {'<'}
      </button>
      {pages.map(page => (
        <button
          key={page}
          onClick={() => onChange(page)}
          style={{
            border: '0.5px solid #184b18',
            background: page === current ? 'linear-gradient(90deg, #05b005d7 0%, #00b050 60%, #90ee90 100%)' : 'transparent',
            color: page === current ? '#fff' : '#184b18',
            borderRadius: 18,
            padding: '0 14px',
            minWidth: 36,
            height: 36,
            fontWeight: 700,
            fontSize: 12,
            outline: 'none',
            cursor: 'pointer',
            transition: 'background 0.2s, color 0.2s',
            boxShadow: 'none',
            margin: '0 2px',
          }}
        >
          {page}
        </button>
      ))}
      <button
        onClick={() => current < pageCount && onChange(current + 1)}
        disabled={current === pageCount}
        style={current === pageCount ? navBtnDisabledStyle : navBtnStyle}
        aria-label="Next page"
      >
        {'>'}
      </button>
    </div>
  );
}